/**
 * The tenancy prefix every application URL carries: `/orgs/$orgId` for organization scoped pages and
 * `/orgs/$orgId/projects/$tenantId` for project scoped ones.
 *
 * Everything that reads ids out of a pathname, or writes them into one, goes through this module so the
 * shape is described once. Route definitions and `navigate({ to, params })` calls keep using the router's
 * own typed patterns; this is for the paths the router cannot type, and for the request layer, which sees
 * only `window.location`.
 *
 * Migration seam: today a URL segment and the identifier the API wants are the same string. If routes
 * ever carry organization and project names or slugs instead, the four functions below are where the
 * two stop being equal, and the lookup belongs here rather than at each of their callers.
 *
 * The two forms are distinct types so that the compiler, rather than a reader, is what notices one
 * being used as the other. `OrgSegment` is what a URL spells; a plain `string` id is what the API and
 * the auth payload use. Neither is assignable to the other, so a value can only cross by going
 * through `resolveOrgId` or `toOrgSegment`.
 */

export const ORG_SEGMENT = 'orgs'
export const PROJECT_SEGMENT = 'projects'

declare const segmentBrand: unique symbol

/** The organization a URL names, exactly as the URL spells it. */
export type OrgSegment = string & { readonly [segmentBrand]: 'org' }

/** The project a URL names, exactly as the URL spells it. */
export type TenantSegment = string & { readonly [segmentBrand]: 'tenant' }

/** Tenancy segments as they come off a URL, before either is resolved to an identifier. */
export interface RouteSegments {
	orgId?: OrgSegment
	tenantId?: TenantSegment
}

/** Ids the URL names. Both are absent off `/orgs/...`; `tenantId` alone is absent at organization level. */
export interface RouteScope {
	orgId?: string
	tenantId?: string
}

const EMPTY_SCOPE: RouteScope = {}

/**
 * Brands a string the router or a pathname produced. The only place a segment is created from an
 * untyped string, which is why it is not exported: everywhere else builds one with `toOrgSegment`.
 */
const asOrgSegment = (value: string) => value as OrgSegment
const asTenantSegment = (value: string) => value as TenantSegment

/**
 * Identifier the API and the auth payload use for the organization a URL segment names.
 *
 * The segment and the identifier are the same string today, so this returns what it is given. It
 * exists because that equality is an assumption rather than a fact, and this is the single place the
 * app makes it: everything downstream compares identifiers to identifiers and needs no knowledge of
 * how a URL spells them.
 */
export function resolveOrgId(segment: OrgSegment): string {
	return segment
}

/** Identifier for the project a URL segment names. See `resolveOrgId`. */
export function resolveTenantId(segment: TenantSegment): string {
	return segment
}

/**
 * URL segment naming an organization, for an identifier the API or the auth payload gave us. The
 * inverse of `resolveOrgId`, and identity for the same reason.
 *
 * Every route parameter minted from data goes through this pair, so that the direction a value is
 * travelling is visible: `resolveOrgId` on the way in, `toOrgSegment` on the way out.
 */
export function toOrgSegment(orgId: string): OrgSegment {
	return asOrgSegment(orgId)
}

/** URL segment naming a project. See `toOrgSegment`. */
export function toTenantSegment(tenantId: string): TenantSegment {
	return asTenantSegment(tenantId)
}

/**
 * The identifiers a URL's tenancy segments name, wherever those segments were read from: a pathname,
 * or the parameters the router already parsed. Each segment resolves on its own, so callers keep
 * whatever rules they have about which combinations are meaningful.
 */
export function resolveRouteScope(segments: RouteSegments): RouteScope {
	const orgId = segments.orgId ? resolveOrgId(segments.orgId) : undefined
	const tenantId = segments.tenantId ? resolveTenantId(segments.tenantId) : undefined
	return { orgId, tenantId }
}

export function parseRouteScope(pathname: string): RouteScope {
	const segments = pathname.split('/').filter(Boolean)
	const orgsIndex = segments.indexOf(ORG_SEGMENT)
	const orgSegment = orgsIndex === -1 ? undefined : segments[orgsIndex + 1]
	if (!orgSegment || orgSegment === PROJECT_SEGMENT) {
		return EMPTY_SCOPE
	}

	// A project segment on its own names nothing, so only the pair is read out of a pathname.
	const tenantSegment = segments[orgsIndex + 2] === PROJECT_SEGMENT ? segments[orgsIndex + 3] : undefined
	const { orgId, tenantId } = resolveRouteScope({
		orgId: asOrgSegment(orgSegment),
		tenantId: tenantSegment ? asTenantSegment(tenantSegment) : undefined,
	})
	return tenantId ? { orgId, tenantId } : { orgId }
}

/** Scope of the page currently on screen. For code outside the router: interceptors, stores, caches. */
export function currentRouteScope(): RouteScope {
	if (typeof window === 'undefined') {
		return EMPTY_SCOPE
	}
	return parseRouteScope(window.location.pathname)
}

export function orgPath(orgId: OrgSegment, suffix = ''): string {
	return `/${ORG_SEGMENT}/${orgId}${suffix}`
}

export function projectPath(orgId: OrgSegment, tenantId: TenantSegment, suffix = ''): string {
	return `/${ORG_SEGMENT}/${orgId}/${PROJECT_SEGMENT}/${tenantId}${suffix}`
}

/** A bare `/projects/$tenantId` prefix predates the org level and still turns up in stored paths. */
const SCOPE_PREFIX = new RegExp(`^/${ORG_SEGMENT}/[^/]+(?:/${PROJECT_SEGMENT}/[^/]+)?|^/${PROJECT_SEGMENT}/[^/]+`)

/** The part of a pathname that names a page, with the tenancy prefix removed: `/data/exporters/all`. */
export function stripScopePrefix(pathname: string): string {
	return pathname.replace(/\/+$/, '').replace(SCOPE_PREFIX, '') || '/'
}

export function isProjectLevelRoute(pathname: string): boolean {
	return parseRouteScope(pathname).tenantId !== undefined
}

/**
 * Modules that exist at organization level. An org URL outside this set (`/orgs/$orgId/access-denied`,
 * or the bare org route) is neither level, which is what keeps owner bypasses and display preferences
 * off pages that have no module of their own.
 */
const ORG_LEVEL_MODULES = new Set(['inventory', 'meters', 'operations', 'billing', 'settings'])

export function isOrganizationLevelRoute(pathname: string): boolean {
	const { orgId, tenantId } = parseRouteScope(pathname)
	if (!orgId || tenantId) {
		return false
	}
	const [module] = stripScopePrefix(pathname).split('/').filter(Boolean)
	return module !== undefined && ORG_LEVEL_MODULES.has(module)
}

/**
 * URLs built outside the router, because the caller opens a new tab, pushes history itself, or hands the
 * string to something that is not a `<Link>`. Route definitions and `navigate({ to, params })` keep using
 * the router's own typed patterns; these are the paths it cannot type.
 */

/** One dashboard viewing one device. Callers encode `deviceId` themselves when it needs it. */
export function dashboardViewPath(
	orgId: OrgSegment,
	tenantId: TenantSegment,
	dashboardId: string | number,
	deviceId: string,
): string {
	return projectPath(orgId, tenantId, `/data/dashboards/view/${dashboardId}/${deviceId}`)
}

export function billingPath(orgId: OrgSegment): string {
	return orgPath(orgId, '/billing/plan-details')
}
